
import { Link } from 'react-router-dom';

const ServicesSection = () => {
  const services = [
    {
      icon: 'ri-lightbulb-line',
      title: 'Technology R&D & Transfer',
      description: 'Connecting you with cutting-edge innovations from Europe and North America, facilitating seamless technology transfer and adaptation for your market.',
      features: [
        'Access to advanced R&D networks',
        'Technology scouting and evaluation',
        'IP licensing and transfer support',
        'Localization and adaptation services'
      ],
      image: 'https://public.readdy.ai/ai/img_res/ca4eb3ba03527b4844df26b497a28b99.jpg'
    },
    {
      icon: 'ri-settings-3-line',
      title: 'OEM/ODM Customization',
      description: 'Tailored manufacturing solutions that bring your vision to life, leveraging our extensive network of trusted partners and quality control expertise.',
      features: [
        'Custom product development',
        'Quality assurance and testing',
        'Supply chain optimization',
        'Scalable production solutions'
      ],
      image: 'https://readdy.ai/api/search-image?query=Modern%20manufacturing%20facility%20with%20precision%20assembly%20line%20and%20quality%20control%20stations%2C%20clean%20industrial%20environment%20with%20robotic%20automation%20and%20skilled%20technicians%2C%20professional%20product%20customization%20workspace%20with%20organized%20tools%20and%20components%2C%20bright%20lighting%20with%20simple%20white%20background%20highlighting%20manufacturing%20excellence&width=600&height=400&seq=service-oem&orientation=landscape'
    },
    {
      icon: 'ri-global-line',
      title: 'Global Market Access',
      description: 'Navigate international markets with confidence through our comprehensive support in compliance, certification, and market entry strategies.',
      features: [
        'Regulatory compliance guidance',
        'Certification and testing support',
        'Market research and analysis',
        'Distribution network development'
      ],
      image: 'https://readdy.ai/api/search-image?query=Global%20business%20network%20visualization%20with%20world%20map%20and%20connected%20nodes%20representing%20international%20trade%20routes%2C%20professional%20corporate%20setting%20with%20digital%20displays%20showing%20market%20data%20and%20analytics%2C%20modern%20office%20environment%20with%20business%20professionals%20collaborating%2C%20clean%20background%20with%20blue%20and%20white%20color%20scheme%20emphasizing%20global%20connectivity&width=600&height=400&seq=service-global&orientation=landscape'
    }
  ];

  return (
    <section className="relative py-32 bg-gradient-to-b from-slate-900/15 to-slate-800/15 overflow-hidden">
      {/* 精致的数据流动背景动画 */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* 垂直数据流 */}
        {[...Array(12)].map((_, i) => (
          <div
            key={`vertical-${i}`}
            className="absolute w-px bg-gradient-to-b from-transparent via-blue-400/20 to-transparent animate-data-flow-down"
            style={{
              left: `${(i + 1) * 8}%`,
              height: '100%',
              animationDelay: `${i * 0.4}s`,
              animationDuration: `${4 + i * 0.2}s`
            }}
          />
        ))}
        
        {/* 水平数据流 */}
        {[...Array(8)].map((_, i) => (
          <div
            key={`horizontal-${i}`}
            className="absolute h-px bg-gradient-to-r from-transparent via-cyan-400/15 to-transparent animate-data-flow-right"
            style={{
              top: `${(i + 1) * 12}%`,
              width: '100%',
              animationDelay: `${i * 0.6}s`,
              animationDuration: `${5 + i * 0.3}s`
            }}
          />
        ))}
        
        {/* 浮动数据点 */}
        {[...Array(16)].map((_, i) => (
          <div
            key={`dot-${i}`}
            className="absolute w-1.5 h-1.5 bg-blue-400/30 rounded-full animate-float-random"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.3}s`,
              animationDuration: `${6 + Math.random() * 4}s`
            }}
          />
        ))}
      </div>

      <div className="relative z-10 container mx-auto px-8">
        <div className="text-center mb-20">
          <div className="inline-block mb-6">
            <span className="px-4 py-2 bg-blue-600/20 text-blue-300 rounded-full text-sm font-medium border border-blue-400/30">
              Our Expertise
            </span>
          </div>
          <h2 className="text-5xl md:text-6xl font-light text-white mb-8 leading-tight">
            Our <strong className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Services</strong>
          </h2>
          <p className="text-white/80 text-xl max-w-4xl mx-auto leading-relaxed font-light">
            Comprehensive technology solutions designed to accelerate your innovation journey and expand your global reach through strategic partnerships
          </p>
        </div>

        <div className="max-w-7xl mx-auto space-y-32">
          {services.map((service, index) => (
            <div
              key={index}
              className={`grid lg:grid-cols-2 gap-16 items-center ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-cyan-600/10 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                  <div className="relative bg-white/8 backdrop-blur-md rounded-3xl p-10 border border-white/20 hover:border-blue-400/40 transition-all duration-500 hover:shadow-2xl hover:shadow-blue-500/10 group-hover:bg-white/12">
                    <div className="flex items-center mb-8">
                      <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-blue-600/30 to-cyan-600/30 rounded-2xl mr-6 group-hover:scale-110 transition-transform duration-300 border border-blue-400/30">
                        <i className={`${service.icon} text-3xl text-blue-300`}></i>
                      </div>
                      <h3 className="text-3xl font-semibold text-white group-hover:text-blue-100 transition-colors duration-300">{service.title}</h3>
                    </div>
                    
                    <p className="text-white/90 leading-relaxed mb-8 text-lg font-light">
                      {service.description}
                    </p>
                    
                    <div className="space-y-4 mb-10">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start group/item">
                          <div className="w-6 h-6 flex items-center justify-center bg-blue-600/20 rounded-lg mr-4 mt-0.5 group-hover/item:bg-blue-600/30 transition-colors duration-300">
                            <i className="ri-check-line text-blue-400 text-sm"></i>
                          </div>
                          <span className="text-white/80 font-medium group-hover/item:text-white transition-colors duration-300">{feature}</span>
                        </div>
                      ))}
                    </div>
                    
                    <Link
                      to="/services"
                      className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600/20 to-cyan-600/20 hover:from-blue-600/30 hover:to-cyan-600/30 border border-blue-400/30 hover:border-blue-400/50 rounded-xl text-blue-300 hover:text-blue-200 transition-all duration-300 font-medium cursor-pointer whitespace-nowrap group/link"
                    >
                      <span>Learn More</span>
                      <i className="ri-arrow-right-line ml-2 group-hover/link:translate-x-1 transition-transform duration-300"></i>
                    </Link>
                  </div>
                </div>
              </div>
              
              <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-cyan-600/20 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500"></div>
                  <div className="relative overflow-hidden rounded-3xl border border-white/20 group-hover:border-blue-400/40 transition-all duration-500">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-96 object-cover object-top transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
